import {
  type CollectionReference,
  type DocumentData,
  type Query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  limit as limitTo,
  orderBy as orderByField,
  query,
  startAfter,
  where,
} from 'firebase/firestore';

/**
 * LA SURFACE DE LISTE DU SOCLE, ecrite UNE fois pour les 56 repositories.
 *
 * Avant ce module, chaque repository portait sa propre copie de
 * `getDocs(query(col, where('orgId','==',orgId)))` : aucun argument, donc
 * chaque ecran chargeait la collection ENTIERE de l'organisation puis
 * filtrait, triait et fenetrait cote client. Sur 15 chats ca ne se voit pas,
 * sur 10 000 contacts c'est 10 000 lectures Firestore, 10 000 validations Zod
 * et un onglet fige. Et `archivedAt` etait ecrit partout mais filtre nulle
 * part : les elements archives remontaient dans les listes.
 *
 * Une seule copie de la semantique, pour que les 56 familles ne puissent pas
 * diverger : le cadrage `orgId` (exige par les Security Rules), l'exclusion des
 * archives, le filtre de sous-type, le tri, la taille de page et le curseur.
 *
 * CE QUE CE MODULE NE FAIT PAS, volontairement :
 *   - aucun tri par defaut. Une requete `list()` sans argument garde EXACTEMENT
 *     l'ordre d'avant (celui de Firestore, par identifiant de document). Poser
 *     un tri par defaut aurait change en silence l'ordre de tous les ecrans
 *     existants, et surtout aurait exige un index composite par famille (voir
 *     la note sur les index plus bas) ;
 *   - aucune recherche plein texte : Firestore n'en fait pas. La recherche
 *     reste cliente, sur les pages deja chargees, et le gabarit le dit a
 *     l'ecran plutot que de laisser croire le contraire.
 *
 * NOTE SUR LES INDEX FIRESTORE, a lire avant d'ajouter un `orderBy` :
 * une requete qui n'a que des filtres d'EGALITE (`orgId`, `archivedAt`, `type`)
 * se sert des index a champ unique que Firestore cree tout seul, meme avec
 * `limit` et `startAfter`. Rien a declarer. Des qu'un `orderBy` porte sur un
 * AUTRE champ que ceux-la, il faut un index composite declare dans
 * `firebase/firestore.indexes.json` et deploye, sinon la requete echoue en
 * production avec `failed-precondition`. `describeMissingIndex` ci-dessous
 * rend le bloc JSON exact a coller. Le fichier d'index ne peut pas etre
 * exhaustif (55 familles multipliees par leurs champs depasseraient le plafond
 * de 200 index composites de Firestore) : il porte les tris reellement utilises,
 * et c'est l'app qui declare le sien quand elle en ouvre un nouveau.
 *
 * LE PIEGE DU CHAMP ABSENT, deux fois le meme mecanisme, deux consequences
 * opposees, et aucune des deux ne fait d'erreur :
 *   - `where('archivedAt', '==', null)` ne rattrape PAS un document ou le champ
 *     est absent : ce document disparait de toutes les listes ;
 *   - `orderBy('status')` EXCLUT tout document ou `status` est absent : trier
 *     par un champ facultatif retire silencieusement des lignes du resultat.
 * Consequence directe a connaitre : `count()` ignore `orderBy` par conception,
 * donc sur un champ facultatif il compte PLUS de documents que la liste triee
 * n'en rendra jamais. Un total et une liste qui ne tombent pas juste ne sont
 * pas forcement un bug de pagination : commencez par regarder si le champ trie
 * est facultatif.
 *
 * LES TROIS ETATS, et la limite assumee du mode Archives. Un document est ACTIF
 * (`archivedAt` null, `deletedAt` null), ARCHIVE (`archivedAt` pose, `deletedAt`
 * null) ou SUPPRIME (les DEUX poses). Supprimer pose aussi `archivedAt` pour une
 * raison tres concrete : le filtre serveur `archivedAt == null` existe deja et
 * les index composites le portent deja, donc un document supprime sort de toutes
 * les listes sans qu'on touche a une seule requete ni a un seul index.
 * En contrepartie, les supprimes remontent dans `archived: 'only'` et
 * `archived: 'all'` (ils PORTENT `archivedAt`), et `runList` les retire COTE
 * CLIENT, apres lecture. Consequence a connaitre avant de la decouvrir en
 * production : UNE PAGE D'ARCHIVES PEUT RENDRE MOINS DE LIGNES QUE LA TAILLE
 * DEMANDEE quand des documents supprimes tombent dedans, et "la page est
 * pleine" reste malgre tout le bon test de "il reste des pages" (il porte sur ce
 * que le SERVEUR a rendu). C'est assume : la corbeille est censee etre vide en
 * usage normal. Le jour ou ca gene, le filtre passe cote serveur, et il faudra
 * alors un `deletedAt` sur TOUS les documents existants, sans quoi le piege du
 * champ absent ci-dessus les ferait tous disparaitre d'un coup : le script
 * `scripts/backfill-deleted-at.mjs` est ecrit pour ca.
 *
 * ET LA MEME CAUSE FRAPPE `count()`, autrement : `runCount` demande une
 * AGREGATION au serveur, il ne lit aucun document, donc il ne peut pas retirer
 * les supprimes apres coup. En clair, sur les modes `'only'` et `'all'`,
 * LE COMPTEUR EST SUPERIEUR AU NOMBRE DE LIGNES AFFICHEES, de exactement le
 * nombre de supprimes. Le mode `'exclude'` (le defaut, donc toutes les listes
 * d'aujourd'hui) est JUSTE : les supprimes portent `archivedAt`, le filtre
 * serveur les a deja sortis. Un ecran d'archives qui affiche "3" au-dessus
 * d'une liste vide n'a donc pas un bug de pagination, il lit un compteur que le
 * serveur calcule sur un critere plus large que celui de la page. Les deux
 * chiffres se rejoignent le jour ou le filtre passe cote serveur, pas avant :
 * d'ici la, un ecran d'archives qui veut un compteur EXACT compte les lignes
 * qu'il a recues plutot que d'appeler `count()`, ce qui n'est vrai que sur une
 * page unique et non paginee.
 */

/** Sens de tri. */
export type ListDirection = 'asc' | 'desc';

/**
 * Ce qu'une liste fait des elements ARCHIVES. Defaut : `'exclude'`.
 *
 *   - `'exclude'` : `where('archivedAt', '==', null)`, les actifs seuls. C'est
 *     le comportement historique, strictement inchange ;
 *   - `'only'`    : `where('archivedAt', '!=', null)`, le mode Archives ;
 *   - `'all'`     : aucun filtre sur `archivedAt`.
 *
 * Les documents SUPPRIMES ne remontent dans aucun des trois : ils portent
 * `archivedAt`, donc `'only'` et `'all'` les ramenent du serveur, et `runList`
 * les retire ensuite cote client (voir l'en-tete du module pour la limite que
 * cela impose a la taille d'une page).
 */
export type ArchivedMode = 'exclude' | 'only' | 'all';

/** Un tri, forme longue. La forme courte est le nom du champ seul. */
export interface ListOrder {
  field: string;
  /** Defaut : `asc`. */
  dir?: ListDirection;
}

/**
 * Les options communes a `list()` sur les 56 familles.
 *
 * Toutes facultatives : `list()` sans argument reste valide et ne casse aucun
 * appelant. Seule difference de comportement avec l'ancienne version, et elle
 * est voulue : les elements ARCHIVES ne remontent plus.
 */
export interface ListOptions {
  /**
   * Sous-type metier de la famille (`type: 'bike'`, `type: 'cat'`). Ignore par
   * les familles qui n'ont pas de discriminant (`contacts`).
   */
  type?: string;
  /**
   * Taille de la page. Absent : aucune limite (comportement d'avant), ce qui
   * reste legitime sur une collection de reglages ou de catalogues.
   */
  limit?: number;
  /**
   * Curseur : l'identifiant du DERNIER document de la page precedente. Le
   * document est relu pour construire le curseur Firestore, ce qui coute une
   * lecture par page et garantit que le curseur reste valide meme avec un tri
   * sur un champ metier.
   */
  after?: string;
  /** Tri serveur. Exige un index composite (voir l'en-tete du module). */
  orderBy?: string | ListOrder;
  /**
   * Inclure les elements archives (`archivedAt` non nul). Defaut : false.
   *
   * @deprecated Utilisez `archived` : `includeArchived: true` vaut
   * `archived: 'all'`, et `includeArchived: false` (ou absent) vaut
   * `archived: 'exclude'`. L'option reste ACCEPTEE et se comporte exactement
   * comme avant : aucun appelant existant n'a a bouger. Elle ne sait juste pas
   * dire `'only'` (le mode Archives), qui est la raison d'etre de `archived`.
   */
  includeArchived?: boolean;
  /**
   * Ce que la liste fait des elements archives. Defaut : `'exclude'`.
   *
   * Si `archived` ET `includeArchived` sont fournis, `archived` GAGNE : c'est
   * l'option precise, `includeArchived` n'est plus qu'un alias historique. On
   * ne leve pas sur la combinaison des deux, parce qu'elle est le cas normal
   * d'un appelant qui migre (il ajoute `archived` a un objet d'options qui
   * portait deja l'ancien drapeau) : lever la ferait echouer une migration qui
   * marche.
   */
  archived?: ArchivedMode;
}

/**
 * Les options de `count()`, VOLONTAIREMENT plus etroites que celles de
 * `list()`.
 *
 * Compter une page n'a pas de sens, et un tri ne change pas un total. Plutot
 * que d'accepter `limit`, `after` et `orderBy` pour les ignorer en silence (un
 * appelant qui reutilise le meme objet d'options pour les deux appels
 * obtiendrait alors un compteur qui ne correspond a rien de ce qu'il a
 * demande), le TYPE les refuse : l'erreur se voit a la compilation.
 *
 * A SAVOIR sur `archived` ici : en `'only'` et `'all'`, le total inclut les
 * documents SUPPRIMES, que la liste retire cote client. Le compte est donc
 * superieur au nombre de lignes rendues. Le detail et la raison sont en tete de
 * ce module ; `'exclude'` (le defaut) n'est pas concerne.
 */
export type CountOptions = Pick<ListOptions, 'type' | 'includeArchived' | 'archived'>;

/**
 * Plafond dur de la taille de page. Une page plus grande n'est pas une page,
 * c'est un chargement complet deguise : autant l'ecrire sans `limit`.
 */
export const MAX_LIST_LIMIT = 1000;

/**
 * Le curseur pointe un document qui n'existe plus (archive en dur, supprime
 * cote serveur, ou identifiant invente).
 *
 * Erreur DEDIEE et non un message generique : l'appelant (le core des ecrans)
 * doit pouvoir la reconnaitre pour repartir de la premiere page au lieu
 * d'afficher une erreur a l'utilisateur. Un repli silencieux sans curseur
 * serait pire que l'erreur : la page suivante reservirait la premiere, et
 * l'utilisateur verrait des doublons sans comprendre.
 */
export class CursorLostError extends Error {
  readonly cursorId: string;
  readonly collection: string;

  constructor(collection: string, cursorId: string, why: string, options?: { cause?: unknown }) {
    super(
      `Curseur de pagination perdu : le document "${cursorId}" de la collection ` +
        `"${collection}" ne peut plus servir de curseur (${why}). ` +
        'Rechargez depuis la premiere page.',
      { cause: options?.cause instanceof Error ? options.cause : undefined },
    );
    this.name = 'CursorLostError';
    this.cursorId = cursorId;
    this.collection = collection;
  }
}

/**
 * Index composite manquant. Classe DEDIEE plutot qu'une `Error` nue, pour deux
 * raisons apprises a la relecture : le `code` de l'erreur Firestore d'origine
 * doit survivre (un appelant distingue `failed-precondition`, qu'il faut
 * corriger, de `unavailable`, qu'il faut reessayer ; l'ecraser fait reessayer
 * en boucle une requete qui ne marchera jamais), et le bloc JSON a coller doit
 * etre lisible sans passer par le texte du message.
 */
export class MissingIndexError extends Error {
  readonly code = 'failed-precondition';
  readonly index: FirestoreIndex;

  constructor(collection: string, index: FirestoreIndex, cause: unknown) {
    super(
      `Index composite manquant pour la collection "${collection}". Ajoutez ce bloc au ` +
        'tableau `indexes` de firebase/firestore.indexes.json, puis deployez ' +
        '(`firebase deploy --only firestore:indexes`) :\n' +
        JSON.stringify(index, null, 2),
      { cause: cause instanceof Error ? cause : undefined },
    );
    this.name = 'MissingIndexError';
    this.index = index;
  }
}

/** Un index composite, dans la forme exacte de `firebase/firestore.indexes.json`. */
export interface FirestoreIndex {
  collectionGroup: string;
  queryScope: 'COLLECTION';
  fields: Array<{ fieldPath: string; order: 'ASCENDING' | 'DESCENDING' }>;
}

/**
 * LE PLAN DE REQUETE : la traduction des options en contraintes, sous forme de
 * donnees pures.
 *
 * Exister separement de la requete Firestore n'est pas une coquetterie : c'est
 * ce qui rend la semantique testable sans emulateur ni projet Firebase (les
 * tests du SDK sont unitaires, `node --test` sur le JS compile). Les regles qui
 * comptent (le cadrage `orgId` est toujours la, les archives sont exclues par
 * defaut) sont ainsi prouvees par un test, pas par une relecture.
 */
export interface ListPlan {
  /** Les filtres d'egalite, dans l'ordre ou ils sont poses. */
  filters: Array<{ field: string; value: unknown }>;
  /**
   * Les filtres d'INEGALITE, dans l'ordre ou ils sont poses. Liste SEPAREE de
   * `filters`, et pas un champ `op` ajoute a chaque entree de celle-ci, pour
   * deux raisons : `filters` est le vocabulaire des egalites partout dans ce
   * module (le Set de `describeMissingIndex`, la traduction en
   * `where(f, '==', v)`), et un champ de plus sur des entrees existantes aurait
   * change la forme d'une structure deja lue et testee ailleurs. Ici, ce qui
   * s'ajoute s'ajoute a cote.
   *
   * A CONNAITRE, c'est la regle Firestore qui decide de tout ici : un champ
   * filtre par inegalite est un champ ORDONNE. Firestore l'ajoute tout seul a
   * la fin des tris explicites (`queryNormalizedOrderBy`, firebase-js-sdk), et
   * l'index composite doit refleter cet ordre : egalites, puis tris demandes,
   * puis champs d'inegalite, puis `__name__`. C'est exactement ce que construit
   * `describeMissingIndex`.
   */
  inequalities: Array<{ field: string; op: '!='; value: unknown }>;
  /**
   * Le mode d'archives RESOLU (`includeArchived` deja traduit, `archived`
   * prioritaire). Porte par le plan et pas recalcule chez l'appelant : `runList`
   * en a besoin APRES lecture pour savoir s'il doit retirer les supprimes, et
   * deux endroits qui resolvent la meme option finissent toujours par diverger.
   */
  archived: ArchivedMode;
  /** Les tris demandes. Vide = ordre naturel de Firestore. */
  order: ListOrder[];
  /** Taille de page, ou null. */
  limit: number | null;
  /** Identifiant du curseur, ou null. */
  after: string | null;
}

/**
 * Resout le mode d'archives d'un jeu d'options. Fonction PURE, un seul endroit.
 *
 * `archived` gagne sur `includeArchived` quand les deux sont fournis (cf. le
 * JSDoc de `ListOptions.archived`). Sans `archived`, l'ancien drapeau garde son
 * sens EXACT : `true` vaut `'all'`, tout le reste vaut `'exclude'`.
 */
function resolveArchivedMode(options: ListOptions): ArchivedMode {
  if (options.archived !== undefined) {
    if (
      options.archived !== 'exclude' &&
      options.archived !== 'only' &&
      options.archived !== 'all'
    ) {
      throw new Error(
        `planListQuery : \`archived\` doit valoir 'exclude', 'only' ou 'all' (recu : ${String(options.archived)}). ` +
          "Une valeur inconnue ne peut pas se rabattre sur un defaut : selon le mode voulu, l'ecran " +
          'montrerait la corbeille a la place de la liste, ou une liste vide sans erreur.',
      );
    }
    return options.archived;
  }
  return options.includeArchived === true ? 'all' : 'exclude';
}

/** Normalise la forme courte (`'name'`) et la forme longue d'un tri. */
function normalizeOrder(order: string | ListOrder): ListOrder {
  const field = typeof order === 'string' ? order : order.field;
  if (typeof field !== 'string' || field.length === 0) {
    throw new Error(
      'planListQuery : `orderBy` doit designer un champ non vide. Un champ vide ne ' +
        'leve pas chez Firestore au moment ou on le pose, il leve plus tard, ' +
        'avec un message qui ne parle plus de tri.',
    );
  }
  const dir = typeof order === 'string' ? 'asc' : (order.dir ?? 'asc');
  return { field, dir };
}

/**
 * Traduit les options en plan de requete. Fonction PURE : aucune dependance
 * Firestore, donc testable seule.
 *
 * L'ordre des filtres est fige et volontaire : `orgId` D'ABORD. Les Security
 * Rules refusent en bloc toute requete `list` qui n'est pas cadree par l'org de
 * l'appelant ; ce cadrage n'est pas une option d'appel, c'est la condition pour
 * que la requete existe.
 */
export function planListQuery(orgId: string, options: ListOptions = {}): ListPlan {
  if (typeof orgId !== 'string' || orgId.length === 0) {
    throw new Error(
      'planListQuery : orgId manquant. Toute requete `list` doit etre cadree par ' +
        "l'organisation, les Security Rules refusent les requetes globales.",
    );
  }

  const filters: ListPlan['filters'] = [{ field: 'orgId', value: orgId }];
  const inequalities: ListPlan['inequalities'] = [];
  const archived = resolveArchivedMode(options);

  // Le sous-type metier, quand la famille en porte un.
  if (options.type !== undefined) {
    if (typeof options.type !== 'string' || options.type.length === 0) {
      throw new Error('planListQuery : `type` doit etre une chaine non vide quand il est fourni.');
    }
    filters.push({ field: 'type', value: options.type });
  }

  // Suppression logique du socle. Le SDK ecrit `archivedAt: null` a la creation
  // et les Security Rules l'EXIGENT : tout document ecrit par un client porte
  // donc le champ. Piege a connaitre quand meme : un document importe par
  // l'Admin SDK sans ce champ serait invisible de toutes les listes, parce que
  // `== null` ne rattrape pas un champ ABSENT. Un import qui oublie
  // `archivedAt` ne produit pas d'erreur, il produit des lignes fantomes.
  //
  // Le mode `'only'` (les Archives) pose une INEGALITE, et elle a exactement le
  // meme angle mort, en pire : `!= null` ne rattrape pas davantage un champ
  // absent, donc un document sans `archivedAt` n'apparait NI dans la liste NI
  // dans les archives. Il n'est nulle part.
  if (archived === 'exclude') {
    filters.push({ field: 'archivedAt', value: null });
  } else if (archived === 'only') {
    inequalities.push({ field: 'archivedAt', op: '!=', value: null });
  }

  const order = options.orderBy != null ? [normalizeOrder(options.orderBy)] : [];

  let limit: number | null = null;
  if (options.limit !== undefined) {
    if (!Number.isInteger(options.limit) || options.limit <= 0) {
      throw new Error(
        `planListQuery : \`limit\` doit etre un entier strictement positif (recu : ${String(options.limit)}).`,
      );
    }
    if (options.limit > MAX_LIST_LIMIT) {
      throw new Error(
        `planListQuery : \`limit\` plafonne a ${MAX_LIST_LIMIT} (recu : ${String(options.limit)}). ` +
          'Pour lire davantage, enchainez les pages avec `after` (le curseur), ' +
          'jamais en agrandissant la page. Retirer `limit` ne corrige rien : ' +
          'sans limite, la requete lit la collection entiere, ce que ce module existe pour eviter.',
      );
    }
    limit = options.limit;
  }

  // Le curseur est interpole dans une reference de document : une chaine vide
  // ou porteuse d'un slash produit une erreur Firestore sur le nombre de
  // segments de chemin, a mille lieues de la cause. L'idiome
  // `after: derniere?.id ?? ''` est trop courant pour ne pas etre attrape ici.
  let after: string | null = null;
  if (options.after !== undefined && options.after !== null) {
    if (typeof options.after !== 'string' || options.after.length === 0) {
      throw new Error(
        'planListQuery : `after` doit etre un identifiant de document non vide. ' +
          "Pour demander la premiere page, omettez `after` (ou passez null), n'envoyez pas une chaine vide.",
      );
    }
    if (options.after.includes('/')) {
      throw new Error(
        `planListQuery : \`after\` doit etre un IDENTIFIANT de document, pas un chemin (recu : "${options.after}").`,
      );
    }
    after = options.after;
  }

  return { filters, inequalities, archived, order, limit, after };
}

/**
 * Le bloc a coller dans `firebase/firestore.indexes.json` pour un plan donne,
 * ou `null` quand Firestore sait deja repondre sans index declare (le cas des
 * requetes a filtres d'egalite seuls).
 *
 * Sert deux fois : a produire le fichier d'index, et a enrichir le message
 * d'erreur quand une requete part sans son index (voir `runList`). Un
 * `failed-precondition` brut de Firestore est un lien vers une console : ici on
 * veut le JSON, parce que l'index vit dans le depot.
 */
export function describeMissingIndex(
  collectionName: string,
  plan: ListPlan,
): FirestoreIndex | null {
  if (plan.order.length === 0 && plan.inequalities.length === 0) return null;

  // Un tri sur un champ DEJA filtre en egalite n'exige aucun index composite
  // (l'egalite fige la valeur, il n'y a plus rien a ordonner). Le declarer
  // quand meme produirait un bloc avec deux fois le meme `fieldPath`, que
  // Firestore refuse comme definition d'index : on aurait remplace une erreur
  // par une autre, plus obscure.
  const equalityFields = new Set(plan.filters.map((filter) => filter.field));
  const explicitSorts = plan.order.filter((entry) => !equalityFields.has(entry.field));

  // Un champ filtre par INEGALITE est un champ ORDONNE pour Firestore : le mode
  // Archives (`archivedAt != null`) exige donc un index composite MEME sans
  // `orderBy`, la ou les egalites seules n'en exigeaient aucun. Deux details
  // qui decident si le bloc rendu marche du premier coup : le tri implicite se
  // place APRES les tris explicites, et il prend le SENS du dernier d'entre eux
  // (c'est ce que fait `queryNormalizedOrderBy` dans firebase-js-sdk, et c'est
  // la requete reellement envoyee qui decide de l'index, pas celle qu'on a
  // ecrite). Un champ deja trie explicitement, ou deja fige par une egalite,
  // n'est pas repris une deuxieme fois : Firestore refuse un index qui porte
  // deux fois le meme `fieldPath`.
  const lastExplicit = explicitSorts[explicitSorts.length - 1];
  const implicitDir: ListDirection = lastExplicit?.dir === 'desc' ? 'desc' : 'asc';
  const explicitFields = new Set(explicitSorts.map((entry) => entry.field));
  const implicitSorts: ListOrder[] = plan.inequalities
    .filter((filter) => !equalityFields.has(filter.field) && !explicitFields.has(filter.field))
    .map((filter) => ({ field: filter.field, dir: implicitDir }));

  const sorts = [...explicitSorts, ...implicitSorts];
  if (sorts.length === 0) return null;

  const last = sorts[sorts.length - 1] as ListOrder;
  const lastOrder: 'ASCENDING' | 'DESCENDING' = last.dir === 'desc' ? 'DESCENDING' : 'ASCENDING';

  const fields: FirestoreIndex['fields'] = [
    ...plan.filters.map((filter) => ({ fieldPath: filter.field, order: 'ASCENDING' as const })),
    ...sorts.map((entry) => ({
      fieldPath: entry.field,
      order: (entry.dir === 'desc' ? 'DESCENDING' : 'ASCENDING') as 'ASCENDING' | 'DESCENDING',
    })),
    // `__name__` EXPLICITE, dans le sens du dernier tri. Firestore ajoute tout
    // seul ce departage a la requete, mais un index declare sans lui est
    // complete en ASCENDING : sur un tri DESCENDING, l'index ne correspond alors
    // a rien et l'erreur revient apres le deploiement. Le bloc rendu ici est
    // celui qu'on colle, il doit marcher du premier coup.
    { fieldPath: '__name__', order: lastOrder },
  ];

  return { collectionGroup: collectionName, queryScope: 'COLLECTION', fields };
}

/**
 * Relit le document du curseur, SANS converter (on n'a besoin que de sa
 * position, pas de sa validation Zod : un document mal forme ailleurs dans la
 * collection ne doit pas casser la page demandee).
 *
 * Par une REQUETE cadree, jamais par un `getDoc`. La raison a ete verifiee sur
 * emulateur et elle est contre-intuitive : nos rules s'ecrivent
 * `allow read: if isOrgMember(resource.data.orgId)`, et sur un document
 * INEXISTANT `resource` vaut null, donc l'evaluation plante et Firestore repond
 * `permission-denied`, pas un snapshot absent. Un `getDoc` ne peut donc jamais
 * rendre `exists() === false` ici : tout le chemin de reprise du curseur perdu
 * aurait ete du code mort, et l'utilisateur aurait vu "Missing or insufficient
 * permissions" sur ce qui n'est qu'un element supprime. Une requete `list`
 * cadree par `orgId`, elle, rend franchement zero resultat.
 */
async function readCursorDoc(
  raw: CollectionReference<DocumentData>,
  orgId: string,
  cursorId: string,
): Promise<QueryDocumentSnapshot<DocumentData>> {
  const found = await getDocs(
    query(raw, where('orgId', '==', orgId), where(documentId(), '==', cursorId), limitTo(1)),
  );
  const first = found.docs[0];
  if (first === undefined) {
    throw new CursorLostError(raw.id, cursorId, 'il n\'existe plus, ou il a quitte cette organisation');
  }
  return first;
}

/**
 * Traduit un plan en contraintes Firestore. Le curseur est resolu ici (une
 * lecture), d'ou l'asynchronisme.
 */
async function toConstraints(
  col: CollectionReference<DocumentData>,
  orgId: string,
  plan: ListPlan,
): Promise<QueryConstraint[]> {
  const constraints: QueryConstraint[] = plan.filters.map((filter) =>
    where(filter.field, '==', filter.value),
  );

  // Les inegalites APRES les egalites : c'est l'ordre dans lequel Firestore
  // veut les champs de l'index composite, et le bloc rendu par
  // `describeMissingIndex` suit exactement le meme ordre.
  for (const filter of plan.inequalities) {
    constraints.push(where(filter.field, filter.op, filter.value));
  }

  for (const entry of plan.order) {
    constraints.push(orderByField(entry.field, entry.dir ?? 'asc'));
  }

  if (plan.after != null) {
    const cursorDoc = await readCursorDoc(collection(col.firestore, col.path), orgId, plan.after);
    try {
      constraints.push(startAfter(cursorDoc));
    } catch (cause) {
      // Firestore refuse un curseur dont le document ne PORTE PAS le champ
      // trie ("you are trying to start ... using a document for which the
      // field X does not exist"). Arrive sur un champ facultatif dont la valeur
      // a ete effacee apres la lecture de la page precedente. C'est un curseur
      // inutilisable, pas une panne : meme reprise que le curseur disparu.
      throw new CursorLostError(
        col.id,
        plan.after,
        'il ne porte plus le champ sur lequel la liste est triee',
        { cause },
      );
    }
  }

  if (plan.limit != null) {
    constraints.push(limitTo(plan.limit));
  }

  return constraints;
}

/**
 * Construit la requete Firestore d'une liste. Exportee pour les cas ou un
 * appelant veut la requete elle-meme (un abonnement temps reel, chantier 3 :
 * il devra s'abonner a la MEME requete, meme cadrage, meme exclusion).
 */
export async function buildListQuery<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: ListOptions = {},
): Promise<Query<T>> {
  const plan = planListQuery(orgId, options);
  const constraints = await toConstraints(
    col as unknown as CollectionReference<DocumentData>,
    orgId,
    plan,
  );
  return query(col, ...constraints);
}

/**
 * EXECUTE une liste : c'est ce que les 56 repositories appellent, et la seule
 * chose qu'ils appellent. Une famille n'a plus rien a decider sur le cadrage,
 * les archives ou la pagination.
 *
 * Le tableau rendu est celui de la page demandee, dans l'ordre du serveur. Il
 * n'y a pas de type de retour "page + curseur" : le curseur de la page suivante
 * est l'identifiant du DERNIER element rendu, et "il reste des pages" se lit
 * "la page est pleine" (`rendus === limit`). Une page finale exactement pleine
 * fait donc afficher un "charger la suite" qui rend une page vide : c'est le
 * defaut connu de ce schema, il se corrige tout seul au clic suivant, et il
 * evite un type de retour de plus dans 56 interfaces.
 */
export async function runList<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: ListOptions = {},
): Promise<T[]> {
  const plan = planListQuery(orgId, options);
  const constraints = await toConstraints(
    col as unknown as CollectionReference<DocumentData>,
    orgId,
    plan,
  );
  try {
    const snap = await getDocs(query(col, ...constraints));
    const rows = snap.docs.map((d) => d.data());
    // Mode 'exclude' : on rend la page telle quelle. AUCUN travail
    // supplementaire sur le chemin de loin le plus frequent (toutes les listes
    // de toutes les apps) : un document supprime porte `archivedAt`, il est
    // donc deja sorti cote SERVEUR par `where('archivedAt', '==', null)`.
    if (plan.archived === 'exclude') return rows;
    // Modes 'only' et 'all' : les supprimes portent `archivedAt`, le serveur
    // les a donc rendus. On les retire ici, apres lecture (voir l'en-tete du
    // module : c'est ce qui peut rendre une page d'archives plus courte que la
    // taille demandee).
    return rows.filter((row) => !isDeleted(row));
  } catch (cause) {
    throw enrichIndexError(col.id, plan, cause);
  }
}

/**
 * Le document est-il SUPPRIME (`deletedAt` pose) ?
 *
 * Le cast local et etroit est volontaire : `deletedAt` vit sur le SOCLE
 * (`baseEntityFields`, @taki/data-model), pas sur le `T` generique de ce module,
 * qui ne connait aucune famille et ne doit surtout pas se mettre a en dependre.
 * On ne lit qu'une cle, on ne suppose rien d'autre de la forme de `T`, et un
 * document historique sans le champ vaut `undefined`, donc NON supprime : c'est
 * exactement le sens voulu.
 */
function isDeleted(row: unknown): boolean {
  return (row as { deletedAt?: unknown } | null)?.deletedAt != null;
}

/**
 * COMPTER sans charger. Le pendant indispensable de la pagination : des qu'un
 * ecran ne charge plus qu'une page, tout compteur calcule sur ce qu'il a en
 * memoire (le "47" d'une fiche, les stats de la zone 3, le compteur du menu)
 * se met a MENTIR, et il ment sans rien signaler. Une liste paginee dont les
 * compteurs comptent la page courante est pire qu'une liste non paginee.
 *
 * `getCountFromServer` compte cote serveur et se facture une lecture par
 * tranche de 1000 documents, pas une par document : c'est ce qui rend un total
 * juste abordable sur un annuaire de plusieurs milliers de lignes.
 *
 * Le compte se fait sur le MEME cadrage que la liste (org, archives, sous-type)
 * mais en IGNORANT `limit`, `after` et `orderBy` : compter une page n'a pas de
 * sens, et un tri ne change pas un total (il ne ferait qu'exiger un index).
 *
 * LA LIMITE, ecrite noir sur blanc plutot que contournee : sur `archived: 'only'`
 * et `archived: 'all'`, ce total compte AUSSI les documents SUPPRIMES. Le filtre
 * des supprimes est cote CLIENT (voir `runList` et l'en-tete du module), et un
 * compte serveur ne peut par construction pas le porter : `getCountFromServer`
 * ne rend qu'un nombre, il n'y a aucun document a filtrer au retour. Les seuls
 * contournements possibles seraient de lire les documents pour les compter (ce
 * que ce module existe precisement pour eviter) ou de rendre un total faux en
 * silence : on documente. En pratique l'ecart vaut la taille de la corbeille,
 * censee etre vide en usage normal, et il disparait le jour ou le filtre passe
 * cote serveur. Le mode `'exclude'`, lui, est exact : un supprime porte
 * `archivedAt`, il est deja hors du compte.
 */
export async function runCount<T>(
  col: CollectionReference<T>,
  orgId: string,
  options: CountOptions = {},
): Promise<number> {
  const plan = planListQuery(orgId, options);
  const constraints: QueryConstraint[] = plan.filters.map((filter) =>
    where(filter.field, '==', filter.value),
  );
  // Meme inegalite que la liste (le mode Archives), pour que le total porte sur
  // le meme cadrage que la page affichee.
  for (const filter of plan.inequalities) {
    constraints.push(where(filter.field, filter.op, filter.value));
  }
  try {
    const snap = await getCountFromServer(query(col, ...constraints));
    return snap.data().count;
  } catch (cause) {
    // Meme enrichissement que `runList`. Ce n'est plus seulement une precaution
    // depuis le mode Archives : un compte n'a jamais de tri, mais il peut porter
    // une INEGALITE (`archivedAt != null`), et une inegalite exige un index
    // composite. Sans cet enrichissement, le compteur du mode Archives d'une
    // famille non declaree rendrait un `failed-precondition` brut, sans le bloc
    // JSON a coller.
    throw enrichIndexError(col.id, plan, cause);
  }
}

/**
 * Un `failed-precondition` Firestore veut dire "index composite manquant", et
 * son message renvoie vers un lien de console. Chez nous l'index vit dans le
 * DEPOT (`firebase/firestore.indexes.json`) : le lien ne sert a rien, le JSON
 * si. On renvoie donc la meme erreur enrichie du bloc a coller.
 */
function enrichIndexError(collectionName: string, plan: ListPlan, cause: unknown): unknown {
  const code = (cause as { code?: unknown } | null)?.code;
  if (code !== 'failed-precondition') return cause;
  const index = describeMissingIndex(collectionName, plan);
  if (index == null) return cause;
  return new MissingIndexError(collectionName, index, cause);
}
